import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { HundredsChart } from './HundredsChart.js';
import { fillThrough } from './fillThrough.js';
import styles from './DayOfSchool.module.css';

// Two hundreds-charts cover days 1–200, the full plausible school
// year. We don't trim the second chart since the source-of-truth for
// "how long is the year" (the district PDF) isn't machine-readable.
const RANGES = [1, 101] as const;

export function DayOfSchool() {
  const api = useApi();
  const { active } = useActiveClassroom();
  const qc = useQueryClient();
  const [rangeStart, setRangeStart] = useState<number>(1);

  const query = useQuery({
    queryKey: ['marked-days', active?.id ?? 'none'],
    queryFn: () => api.dayOfSchool.list(active!.id),
    enabled: active !== null,
  });

  const replace = useMutation({
    mutationFn: (days: number[]) => api.dayOfSchool.replace(active!.id, days),
    onMutate: async (days) => {
      await qc.cancelQueries({ queryKey: ['marked-days', active?.id ?? 'none'] });
      const prev = qc.getQueryData<number[]>(['marked-days', active?.id ?? 'none']);
      qc.setQueryData(
        ['marked-days', active?.id ?? 'none'],
        [...days].sort((a, b) => a - b),
      );
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev !== undefined) {
        qc.setQueryData(['marked-days', active?.id ?? 'none'], ctx.prev);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['marked-days', active?.id ?? 'none'] }),
  });

  const markedSet = useMemo(() => new Set<number>(query.data ?? []), [query.data]);

  // Default-open to the 101–200 chart when this classroom already has
  // any marked day past 100 — saves the teacher a tap when they're
  // deep in the school year. We only auto-pick on first-load per
  // classroom (tracked via the ref), so the teacher's manual range
  // taps stick afterwards.
  const initializedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!active || !query.data) return;
    if (initializedFor.current === active.id) return;
    initializedFor.current = active.id;
    const max = query.data.length > 0 ? Math.max(...query.data) : 0;
    setRangeStart(max > 100 ? 101 : 1);
  }, [active, query.data]);

  if (!active) {
    return (
      <section className={styles.empty} role="status">
        No classroom selected.
      </section>
    );
  }

  // Tapping a day counts up to it (filling any missed days) rather than
  // toggling a single cell — so the teacher marks "today" with one tap and any
  // days they forgot get caught up. Tapping an already-counted day steps the
  // count back to just before it. See fillThrough() for the full rules.
  function handleTap(day: number) {
    replace.mutate(fillThrough(day, markedSet.has(day)));
  }

  const count = markedSet.size;

  return (
    <section className={styles.dayOfSchool} data-testid="day-of-school-page">
      <div className={styles.card}>
        <h1 className={styles.title} data-testid="dos-count">
          Today is the <span className={styles.count}>{count}</span> day of school
        </h1>

        <fieldset className={styles.rangeRow}>
          <legend className={styles.srOnly}>Range</legend>
          {RANGES.map((start) => (
            <button
              key={start}
              type="button"
              onClick={() => setRangeStart(start)}
              className={styles.rangeButton}
              aria-pressed={rangeStart === start}
              data-testid={`dos-range-${start}`}
            >
              {start}–{start + 99}
            </button>
          ))}
        </fieldset>

        <HundredsChart rangeStart={rangeStart} markedDays={markedSet} onToggle={handleTap} />
      </div>
    </section>
  );
}
